<template>
  <div class="instruction-content" v-if="!$store.getters.isSdkValid">
    <div v-if="index">
      <h3 class="instruction-subheader">1. Remove Old Rotator Code</h3>
      <p class="instruction-paragraph">Your page includes a Rotator SDK that is either outdated or tied to a different rotation group. Find and remove the following code before continuing.</p>
      <div class="code-preview">
        <p id="code-explanation">
          <pre v-if="$store.state.rotator.script_location">{{
             $store.state.rotator.script_location[0]
            }}<div class="existing-sdk">{{
              "\n" + $store.state.rotator.script_location[1] + "\n"
             }}</div>{{
            $store.state.rotator.script_location[2]
          }}</pre>
          <pre v-else>
            &lt;!DOCTYPE HTML&gt;
            &lt;html&gt;
            &nbsp;&nbsp;&nbsp;&nbsp;&lt;head&gt;
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<div class="existing-sdk">{{
              "\n" + this.$store.getters.sdk() + "\n\n"
             }}</div>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;....
            &nbsp;&nbsp;&nbsp;&nbsp;&lt;/head&gt;
          </pre>
        </p>
      </div>
    </div>
    <h3 class="instruction-subheader">{{ index + 1 }}. Copy Rotator SDK</h3>
    <p class="instruction-paragraph">Copy the code below, which is unique to this page.</p>
    <div class="code-preview" v-loading="!$store.state.rotator.template_id" element-loading-text="Loading...">
      <textarea id="code-top-copy" rows="5">{{ this.$store.getters.sdk() }}</textarea>
      <div class="tool-bar__default row">
        <button class="copy-btn__default" @click="copyToClipboard" :style="{'background-color': [copied == true ? 'green' : '#9c9c9c']}">{{ copyButton }}</button>
      </div>
    </div>
    <h3 class="instruction-subheader">{{ index + 2 }}. Place Rotator SDK</h3>
    <p class="instruction-paragraph">Paste the copied code into the top of your webpage source, just below the opening &lt;head&gt; tag.</p>
    <div class="code-preview">
      <p id="code-explanation">
        <pre>
&lt;!DOCTYPE HTML&gt;
&lt;html&gt;
&nbsp;&nbsp;&nbsp;&nbsp;&lt;head&gt;
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<div class="new-sdk">{{
  "\n" + this.$store.getters.sdk().replace(/^(\s*[^\s])/mg, (s) => "&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;" + s) + "\n\n"
}}</div>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;....
&nbsp;&nbsp;&nbsp;&nbsp;&lt;/head&gt;
        </pre>
      </p>
    </div>
    <h3 class="instruction-subheader">{{ index + 3 }}. Verify Setup</h3>
    <p class="instruction-paragraph">After publishing your page with above changes, click on the button below to verify that the script is installed and running correctly.</p>
    <button class="form-button__default form-button__largetext" v-on:click="liveDetect()">
      <transition name="fade" mode="out-in">
        <div v-if="this.$store.state.rotator.sdkDetection" class="el-loading-spinner">
          <svg viewBox="25 25 50 50" class="circular">
            <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
          </svg>
        </div>
        <div v-else>Test Integration</div>
      </transition>
    </button>
    <transition name="fade" mode="out-in">
      <div class="error-sdk" :key="errorMessage">
        <p class="instruction-paragraph">{{ errorMessage }}</p>
      </div>
    </transition>
  </div>
  <div v-else>
    <div class="instruction-content">
      <h2 class="instruction-notice">Congratulations! A valid Rotator SDK was detected on this page.</h2>
      <h3 class="instruction-subheader">1. Save Changes</h3>
      <p class="instruction-paragraph">No code modifications are required. If you're satisfied with the changes you've made, simply click on the "SAVE" button below and they will be published instantly.</p>
      <button class="form-button__default form-button__largetext" v-on:click="save()">
        <transition name="fade" mode="out-in">
          <div v-if="saving" class="el-loading-spinner">
            <svg viewBox="25 25 50 50" class="circular">
              <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
            </svg>
          </div>
          <div v-else>Save</div>
        </transition>
      </button>
      <div v-show="saveMessage" class="error-sdk">{{ saveMessage }}</div>
    </div>
  </div>
  <!-- End of Download/Save Section -->
</template>

<script>
export default{
  name: 'Instruction',

  data(){
    return{
      saving            : false,
      copied            : false,
      errorMessage      : null,
      saveMessage       : null
    }
  },
  computed: {
    copyButton() {
      return this.copied? 'Copied !': 'Copy to Clipboard'
    },
    index() {
      return this.$store.state.rotator.sdkDetected? 1: 0;
    }
  },

  mounted() {
    this.$store.dispatch('setTemplateId', this.$store.state.rotator.template_id)
  },

  watch: {
    '$store.state.rotator.sdkDetection'(newVal, oldVal) {
      if (newVal === false) {
        this.errorMessage = 'The Rotator SDK from above was not detected on your page. Please double check the installation, and try again.'
      } else {
        this.errorMessage = ''
      }
    }
  },
  methods:{
    liveDetect() {
      this.$store.commit('sdkDetection', true)
    },
    save() {
      this.saving       = true
      this.saveMessage  = ''

      let endpoint  = this.$store.getters.templateBaseEndpoint
      let params    = new URLSearchParams({ selector_groups: this.$store.getters.getSelectionJson() })

      let success   = false
      this.$http.put(endpoint, params).then((response) => {
        this.saveMessage = 'Successfully saved!'
        this.saving = false
      }).catch((error) => {
        this.saveMessage = 'Sorry, failed to save!'
        this.saving = false
      })
    },
    copyToClipboard() {
      try {
        const copyText = document.getElementById("code-top-copy");
        copyText.select();
        document.execCommand("Copy");

        this.copied = true
        setTimeout(() => this.copied = false, 5000)
      } catch (err) {
        //
      }
    }
  }
}
</script>
<style lang="scss">
.instruction-content .el-loading-mask {
  background-color: rgba(250, 250, 250, .8);

  .el-loading-text {
    text-align: center;
    font-size: 16px;
    font-weight: bold;
    padding: 6px;
  }
  .el-loading-spinner {
    margin-top: -41px;
  }
}
</style>
<style lang="scss" scoped>
@import '~static/mixins/global';
@import '~static/mixins/variables';

.form-button__default {
  width: 250px;
  text-align: center;
  margin: 25px 0 0 0;
}

.el-loading-spinner {
  position: relative;
  height: 21px;
  margin-top: 0;
  top: -6px;
  left: -1px;
}
.el-loading-spinner .circular{
  display: inline-block;
  vertical-align: -webkit-baseline-middle;
  overflow: hidden;
  height: 30px;
  width: 30px;
}
.el-loading-spinner .path {
  stroke: #fff;
}

.transition-reset{
  display: block;
  width: 100%;
}
.step-list__element{
  transition: all .1s;
}

.section-enter-active, .section-leave-active {
  @include transitionBundle(all, .6s);
}
.section-leave-active{
  opacity: 0;
  visibility: hidden;

  @include placementBundle(absolute, 0, initial, initial, 0);
}
.section-enter-active{
  opacity: 0;
  height: 0;

  @include placementBundle(absolute, 0, initial, initial, 95px);
}
.section-leave-to{
  opacity: 0;
  left: 0px;
}
.section-enter-to{
  opacity: 1;
  left: 0;
}

.copy-btn__default {
  transition: background-color 1000ms linear;
  width: 130px;
  text-align: center;
}

.existing-sdk, .new-sdk {
  color: red;
  border: 3px solid #F56C6C;
  overflow: hidden;
}
.new-sdk {
  border-color: #67C23A;
  color: green;
}
</style>



// WEBPACK FOOTER //
// Instruction.vue?6b86e336